import { convertWarekiDetail } from '@/utils/wareki';

/**
 * RS-21 — Japanese text for the Shinsa block on 「返戻・問合せレセプト登録」.
 *
 * The labels themselves live in `@/constants/shinsaStatus` (RS-10) — one vocabulary, two screens, so
 * レセプト発行 and 返戻処理 can never call the same state by different names.
 */
export const RETURN_SHINSA_COLUMNS = {
  /** Deliberately named after the other system, so it cannot be confused with RAH's own 状況. */
  STATUS: '審査状況',
  /** Short on purpose: AppTable pins header cells to white-space:nowrap, so the HEADER TEXT is the
   *  column's minimum width. Measured on this 13-column screen, 「返戻区分」+「返戻確定日」 cost 145px of
   *  pure header — taken straight out of the 操作 column, which collapsed to 39px and made its three
   *  buttons unusable. The screen is 返戻処理, so 返戻 is already the context. */
  RETURN_TYPE: '区分',
  /** The day Shinsa decided, not the day RAH received the message — so it is bound to
   *  `shinsa_return_date` (the meta's 返戻日), NOT to `shinsa_status_at`. */
  DECIDED_AT: '返戻日',
} as const;

/** Highlight applied to the row a mailbox deep link points at. */
export const RETURN_DEEP_LINK_PARAM = 'rezept_id';

/**
 * "20260310" → "令和08年03月10日". Anything unexpected is shown untouched, never guessed at.
 *
 * 和暦 on purpose, and with the SAME shape as 施術年月 on the same row (「令和08年08月」, era + 2-digit year,
 * zero-padded parts) so the two date columns read as one convention — only the 日 is added, because this
 * column is a day, not a month. Nothing is dropped: year, month AND day are all shown.
 *
 * TWO input shapes on purpose. The compact one is what this column now receives: Shinsa's own GIVEBACK_DATE,
 * passed through the meta without reformatting. The dashed one is kept because the column was bound to
 * `shinsa_status_at` until 2026-08-05, and a cached response or a rolled-back build must not render a raw
 * timestamp at a clinic.
 *
 * `convertWarekiDetail` (not Intl) is the era source: it is the same table the datepickers use, and it
 * returns '' for a date before 明治 — in which case the Gregorian slashes are kept rather than inventing
 * an era.
 */
export function formatShinsaDecidedAt(value?: string | null): string {
  if (!value) return '';
  const compact = /^(\d{4})(\d{2})(\d{2})$/.exec(value.trim());
  const dashed = compact ? null : /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  const parts = compact ?? dashed;
  if (!parts) return value;

  const [, year, month, day] = parts;
  const era = convertWarekiDetail(Number(year), Number(month), Number(day));
  return era ? `${era}年${month}月${day}日` : `${year}/${month}/${day}`;
}
